import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { HolidayKind } from "../constants/holiday.constants";
import { generateWeeklyOffs } from "../services/HolidayService";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - 1 + i);

const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

type Props = {
  show: boolean;
  year: number;
  onClose: () => void;
  onSuccess: () => void;
};

export function WeeklyOffsModal({ show, year, onClose, onSuccess }: Props) {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      year,
      weekdays: [] as number[],
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.weekdays.length) {
        errors.weekdays = "Select at least one weekday.";
      }
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await generateWeeklyOffs({
          year: values.year,
          weekdays: values.weekdays,
          kind: HolidayKind.COMPANY,
        });
        if (res.success) {
          const d = res.data as any;
          toast.success(
            `Done — ${d?.created ?? 0} created, ${d?.skipped ?? 0} skipped.`,
          );
          onSuccess();
          resetForm();
          onClose();
        } else {
          toast.error(res.message ?? "Could not generate weekly offs.");
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to generate weekly offs.",
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const toggleWeekday = (val: number) => {
    const current = formik.values.weekdays;
    formik.setFieldValue(
      "weekdays",
      current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val],
    );
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>Generate weekly off days</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Enumerates every matching weekday in the selected year and creates
          holiday entries, skipping any dates that already exist.
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Label className="form-label" htmlFor="wo-year">
                Year
              </Label>
              <Input
                id="wo-year"
                name="year"
                type="select"
                className="form-select"
                value={formik.values.year}
                onChange={(e) =>
                  formik.setFieldValue("year", Number(e.target.value))
                }
                onBlur={formik.handleBlur}
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          <div className="mb-3">
            <Label className="form-label d-block">Weekdays</Label>
            <div className="d-flex flex-wrap gap-3">
              {WEEKDAYS.map((day) => (
                <div className="form-check" key={day.value}>
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id={`wo-day-${day.value}`}
                    checked={formik.values.weekdays.includes(day.value)}
                    onChange={() => toggleWeekday(day.value)}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor={`wo-day-${day.value}`}
                  >
                    {day.label}
                  </Label>
                </div>
              ))}
            </div>
            {formik.touched.weekdays && formik.errors.weekdays ? (
              <div className="text-danger small mt-1">
                {formik.errors.weekdays}
              </div>
            ) : null}
          </div>

          <div className="d-flex justify-content-end gap-2 pt-2">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              Generate
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
